<template lang="pug">

  transition(
    :css="false" 
    @enter="enter"
    @leave="leave"
    appear
  )

    button(
      v-if="show"
      @click="onClick"
      @mouseenter="onMouseEnter"
      @touchstart="onTouchStart"
      @focus="onMouseEnter"
      @mouseleave="onMouseLeave"
      @touchend="onTouchEnd"
      @blur="onMouseLeave"
      v-bind:aria-expanded="isMenuOpen ? 'true' : 'false'"
      aria-haspopup="true"
      aria-label="Menu"
      aria-controls="menu"
      :disabled="isDisabled"
      :class="{ 'menu-open' : isMenuOpen }"
    ).hamburger
      .bg(ref="bg")
      .inner(ref="inner")
        hamburger-svg( ref="hamburger" v-show="!isMenuOpen" )
        close-svg( ref="close" v-show="isMenuOpen" )

</template>

<script>

import gsap from 'gsap'
import { mapState, mapMutations } from 'vuex'
import Env from 'src/config/env'
import { MODAL_STATES, PATHS , EVENTS } from 'src/config/app' 
import CloseSvg from '@/components/common/CloseSvg'
import HamburgerSvg from '@/components/common/HamburgerSvg'
import Communicator from '@/helpers/Communicator'

export default {

  computed: {

    ...mapState('app', ['loading', 'home', 'modal_state', 'motion_clamp']),

    isMenuOpen(){
      return (this.modal_state === MODAL_STATES.MENU) && this.show
    },

    show(){
      let isWelcome = (this.home.section_index === 0)
      let mobile = (!isWelcome && Env.mobile && this.isHomeRoute)
      let desktop = (!isWelcome && ( Env.desktop || Env.tablet ) )
      return mobile || desktop
    },

    isHomeRoute(){
      return this.$route.name === PATHS.HOME
    },

    isDisabled(){
      return !this.isHomeRoute
    }
  },

  components: { 
    CloseSvg,
    HamburgerSvg
  },

  methods: {

    ...mapMutations('app',[ 'setModalState']),

    onClick(){
      let state = (this.modal_state === MODAL_STATES.NONE) ? MODAL_STATES.MENU : MODAL_STATES.NONE
      this.setModalState(state)
    },

    onMouseEnter( ){ 

      if(Env.touch_device){
        return 
      }

      this.mouseenter()
    },

    onMouseLeave( ){
      
      if(Env.touch_device){
        return 
      }

      this.mouseleave()
    },

    onTouchStart(){
      this.mouseenter()
    },

    onTouchEnd(){
      this.mouseleave()
    },

    mouseenter(){

      if(this.isDisabled){
        return 
      }

      Communicator.trigger(EVENTS.MOUSE_ENTER)
      gsap.killTweensOf([this.$refs.bg, this.$refs.inner])
 
      this.$refs.close.mouseenter() 
      this.$refs.hamburger.mouseenter() 

      gsap.to(this.$refs.bg, { duration: 0.3 * this.motion_clamp, opacity: 1, scale: 1, ease: 'expo.inOut' })  
      gsap.to(this.$refs.inner, { duration: 0.3 * this.motion_clamp, scale: 0.9, ease: 'expo.inOut' })  
    },

    mouseleave(){

      if(this.isDisabled){
        return 
      }

      Communicator.trigger(EVENTS.MOUSE_LEAVE)
      gsap.killTweensOf([this.$refs.bg, this.$refs.inner])
       
      this.$refs.close.mouseleave() 
      this.$refs.hamburger.mouseleave(0.25) 

      gsap.to(this.$refs.bg, { duration: 0.3 * this.motion_clamp, opacity: 0, scale: 0.8, ease: 'expo.inOut' })  
      gsap.to(this.$refs.inner, { duration: 0.3 * this.motion_clamp, scale: 1, ease: 'expo.inOut' })  
    },


    enter( el, done ){ 
      gsap.killTweensOf(this.onLeaveComplete) 
      done()
    },

    leave( el, done ){
      gsap.killTweensOf(this.onLeaveComplete)
      this.$refs.hamburger.leave()
      gsap.delayedCall(1.4 * this.motion_clamp, this.onLeaveComplete, [done]) 
    },

    onLeaveComplete(done){
      done()
    }
  },

  watch: {
    isDisabled(val){
      gsap.killTweensOf(this.$refs.inner)
      gsap.to(this.$refs.inner, { 
        duration: 1 * this.motion_clamp,
        opacity: val ? 0.2 : 1
      }) 
    }
  }
}

</script>

<style lang="sass">
@import "src/styles/common.sass"

$button-height: 44px
$button-width: 44px
$svg-height: 20px
$svg-width: 25px

$margin-desktop-left: calc( #{$svg-width - $button-width} * 0.5 )
$margin-desktop-top: calc( #{$svg-height - $button-height} * 0.5 )
$margin-mobile-left: calc(4rem + #{$margin-desktop-left})
$margin-mobile-top: calc(3.7rem + #{$margin-desktop-top})

.hamburger
  pointer-events: auto
  position: relative
  width: $button-width 
  height: $button-height
  display: flex
  align-items: center
  justify-content: center
  left: $margin-desktop-left
  top: $margin-desktop-top
  outline: none
  opacity: 1  

  .bg
    transform: scale(0.8)
    background: white
    border-radius: 50%
    opacity: 0
    position: absolute
    top: 0
    height: 100%
    width: 100%


  &.menu-open
    .bg
      background: #282C68
  
  &:disabled 
    pointer-events: none

  @media all and (max-width: $bp-mobile)
    position: absolute
    top: $margin-mobile-top
    left: $margin-mobile-left
 
  svg 
    line
      stroke: white

  .inner
    position: relative
    width: 22px
    height: 22px
    display: flex
    align-items: center

    svg
      position: absolute
      width: 100%
      height: 100%
      overflow: visible

      .stroke-one
        stroke: #282C68
      .stroke-two
        stroke: #373C85


    &.white
      svg
        .stroke-one,
        .stroke-two
          stroke: white




</style>
